export class ScannerImageUploadTask {
    constructor(scannedImagesLocation) {
        this.name = 'ScannerImageUploadTask'
        this.optionName = 'scanner-image-uploads-2026-02'
        this.scannedImagesLocation = scannedImagesLocation
        this.startDate = new Date('2026-01-01')
        this.endDate = new Date('2026-02-18')
        this.loopTimeout = 100
    }

    async run({ fetcher, user, platform }) {
        if (!platform.isDesktopApp) {
            console.log('ScannerImageUploadTask: Not a desktop app, skipping.')
            return
        }

        try {
            const optionResponse = await fetcher
                .authenticatedFetch({
                    method: 'get',
                    path: `/user-options/${this.optionName}`,
                    responseHandler: (response) => response.data,
                })
                .catch((err) => {
                    if (err.status === 404) {
                        return null
                    }
                    throw err
                })

            if (optionResponse && optionResponse.value === 'done') {
                console.log('ScannerImageUploadTask: Task already marked as done.')
                return
            }

            if (!platform.existsSync(this.scannedImagesLocation)) {
                console.log(`ScannerImageUploadTask: Path ${this.scannedImagesLocation}oes not exist.`)
                return
            }

            await this.startIdleUploadLoop({ fetcher, user, platform })
        }
        catch (error) {
            console.error('ScannerImageUploadTask: Error during task execution:', error)
        }
    }

    async startIdleUploadLoop(context) {
        const { fetcher, user, platform } = context

        const processedFiles = []

        const iterate = async () => {
            try {
                const files = platform.readdirSync(this.scannedImagesLocation)
                const matchingFiles = files
                    .filter((fileName) => {
                        return !processedFiles.includes(fileName)
                    })
                    .filter((fileName) => {
                        if (!fileName.toLowerCase().endsWith('.jpg') && !fileName.toLowerCase().endsWith('.tif')) {
                            return false
                        }
                        const fullPath = `${this.scannedImagesLocation}${fileName}`
                        const stats = platform.statSync(fullPath)
                        const birthtime = new Date(stats.birthtime)
                        return birthtime >= this.startDate && birthtime < this.endDate
                    })

                console.log(`ScannerImageUploadTask: Found ${matchingFiles.length} matching files:`, matchingFiles)

                let fileToUpload = null
                for (const fileName of matchingFiles) {
                    const existsResponse = await fetcher.authenticatedFetch({
                        method: 'get',
                        path: '/user-files',
                        params: {
                            filters: {
                                file: {
                                    name: fileName,
                                },
                            },
                        },
                        responseHandler: (response) => response.data,
                    })

                    if (existsResponse.data.length === 0) {
                        fileToUpload = fileName
                        break
                    }
                    processedFiles.push(fileName)
                }

                if (!fileToUpload) {
                    console.log('ScannerImageUploadTask: No more files to upload. Marking as done.')
                    await fetcher.authenticatedFetch({
                        method: 'put',
                        path: `/user-options/${this.optionName}`,
                        data: { value: 'done' },
                    })
                    return
                }

                console.log(`ScannerImageUploadTask: Uploading ${fileToUpload}...`)
                const fullPath = `${this.scannedImagesLocation}${fileToUpload}`
                const fileBuffer = platform.readFileSync(fullPath)
                const formData = new FormData()

                const fileExtension = fileToUpload
                    .toLowerCase()
                    .split('.')
                    .pop()
                let imageType = undefined
                if (fileExtension === 'tif') {
                    imageType = 'image/tiff'
                }
                else {
                    imageType = 'image/jpeg'
                }

                // Since it's a Buffer from Electron's fs, we need to wrap it for FormData
                const blob = new Blob([fileBuffer], { type: imageType })
                formData.append('files.file', blob, fileToUpload)
                formData.append('data', JSON.stringify({
                    purpose: this.optionName,
                }))

                await fetcher.authenticatedFetch({
                    method: 'post',
                    path: '/user-files',
                    data: formData,
                })

                processedFiles.push(fileToUpload)

                console.log(`ScannerImageUploadTask: Finished uploading ${fileToUpload}. Triggering next idle opportunity.`)
                // Use requestIdleCallback or setTimeout to trigger next iteration
                const nextIteration = () => {
                    setTimeout(() => iterate(), this.loopTimeout)
                }
                nextIteration()
            }
            catch (error) {
                console.error('ScannerImageUploadTask: Error in idle loop:', error)
            }
        }

        const startLoop = () => {
            setTimeout(() => iterate(), this.loopTimeout)
        }
        startLoop()
    }
}
